import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import ApprovedPatientCard from '../../components/ApprovedPatientCard';
import NHCLogo from '@design/NHCLogo';
import { WebEntryLayout } from './entryHelpers';

const slides = [
  { title: 'One Health Identity', subtitle: 'Your universal NeoHealthCard — one identity across every provider.' },
  { title: 'Your Records, Your Control', subtitle: 'Consent-based sharing. Every access is logged. You decide who sees what.' },
  { title: 'Book Doctor, Lab, Pharmacy, Ambulance', subtitle: 'All care services in one place — book, track, and manage seamlessly.' },
  { title: 'NeoAI + Emergency Care', subtitle: 'AI-powered health guidance and one-tap emergency SOS with live tracking.' },
];

const serviceGrid = [
  { label: 'Doctor', color: T.navy, icon: 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z' },
  { label: 'Lab', color: T.purple, icon: 'M9 3h6 M10 3v6.5L5.5 18a2 2 0 001.8 3h9.4a2 2 0 001.8-3L14 9.5V3' },
  { label: 'Pharmacy', color: '#059669', icon: 'M10.5 20.5 4 14a4.95 4.95 0 0 1 7-7l6.5 6.5a4.95 4.95 0 0 1-7 7Z M8.5 8.5l7 7' },
  { label: 'Ambulance', color: T.danger, icon: 'M3 7h11v8H3z M14 10h4l3 3v2h-7 M5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M17 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z' },
];

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);

  const isLast = slide === 3;

  return (
    <WebEntryLayout>
    <div
      style={{
        flex: 1,
        background: T.white,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px 28px 80px',
        fontFamily: T.font,
        position: 'relative',
      }}
    >
      {/* Slide content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        {/* Slide 0: NHC Card */}
        {slide === 0 && (
          <div style={{ width: '100%', maxWidth: 400 }}>
            <ApprovedPatientCard
              name="Rajesh Kumar Sharma"
              nhcId="2487 6091 3352"
              bloodGroup="A+"
              insuranceStatus="Linked"
            />
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: T.textTer, fontFamily: T.font }}>
              Sample card for illustration — yours is issued after registration
            </div>
          </div>
        )}

        {/* Slide 1: Lock icon */}
        {slide === 1 && (
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: 24,
              background: `${T.teal}1F`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width={36} height={36} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
              <circle cx="12" cy="16" r="1" />
            </svg>
          </div>
        )}

        {/* Slide 2: 2x2 service grid */}
        {slide === 2 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 200 }}>
            {serviceGrid.map((s) => (
              <div
                key={s.label}
                style={{
                  width: 72,
                  height: 56,
                  borderRadius: 14,
                  background: `${s.color}14`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d={s.icon} />
                </svg>
                <div style={{ fontSize: 8.5, fontWeight: 600, color: s.color, fontFamily: T.font }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Slide 3: NeoAI + Emergency */}
        {slide === 3 && (
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: `#7C3AED1F`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <NHCLogo w={24} />
            </div>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: `${T.danger}1F`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </div>
          </div>
        )}

        {/* Title & subtitle */}
        <div style={{ fontSize: 18, fontWeight: 800, color: T.text, textAlign: 'center', marginTop: 28, lineHeight: 1.3 }}>
          {slides[slide].title}
        </div>
        <div style={{ fontSize: 11, color: T.textSec, textAlign: 'center', lineHeight: 1.7, marginTop: 8, maxWidth: 280 }}>
          {slides[slide].subtitle}
        </div>
      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {slides.map((_, i) => (
          <div
            key={i}
            style={{
              width: i === slide ? 20 : 8,
              height: 8,
              borderRadius: 4,
              background: i === slide ? T.teal : T.border,
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10, width: '100%', maxWidth: 340 }}>
        {!isLast && (
          <button
            onClick={() => navigate('/patient/register/start', { replace: true })}
            style={{
              flex: 1,
              padding: '14px 0',
              background: T.white,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              borderRadius: 14,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: T.font,
              cursor: 'pointer',
            }}
          >
            Skip
          </button>
        )}
        <button
          onClick={() => {
            if (isLast) {
              navigate('/patient/register/start', { replace: true });
            } else {
              setSlide(slide + 1);
            }
          }}
          style={{
            flex: 2,
            padding: '14px 0',
            background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
            color: T.white,
            border: 'none',
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: T.font,
            cursor: 'pointer',
          }}
        >
          {isLast ? 'Get Started' : 'Next'}
        </button>
      </div>
    </div>
    </WebEntryLayout>
  );
}
